import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronLeft, Send, Car, Calendar, Headphones, 
  User, Lock, ExternalLink, Shield, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getProfilePhotoUrl, getVehiclePhotoUrl } from '../lib/imageUtils';

const ChatPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const isPlatformOwner = user?.roles?.includes('platform_owner');

  useEffect(() => {
    fetchThread();
    // Mark as read when opening
    markAsRead();
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchThread = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/messages/threads/${threadId}`);
      setThread(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load conversation');
      navigate(isPlatformOwner ? '/support-inbox' : '/messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await api.post(`/messages/threads/${threadId}/read`);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await api.post(`/messages/threads/${threadId}/send`, {
        body: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      textareaRef.current?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const getOtherParticipant = () => {
    const others = thread?.participants?.filter(p => p.user_id !== user?.user_id) || [];
    return others[0] || { full_name: 'Unknown' };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(parseISO(timestamp), 'd MMM yyyy, h:mm a');
    } catch {
      return '';
    }
  };

  const getThreadTypeIcon = (type) => {
    switch (type) {
      case 'inquiry': return <Car className="w-4 h-4" />;
      case 'booking': return <Calendar className="w-4 h-4" />;
      case 'support': return <Headphones className="w-4 h-4" />;
      default: return null;
    }
  };

  const getThreadTypeLabel = (type) => {
    switch (type) {
      case 'inquiry': return 'Vehicle Inquiry';
      case 'booking': return 'Booking Discussion';
      case 'support': return 'Support';
      default: return type;
    }
  };

  // Get the back navigation path
  const getBackPath = () => {
    // If platform owner viewing from support inbox, go back there
    if (isPlatformOwner && thread?.is_platform_owner_view) {
      return '/support-inbox';
    }
    return '/messages';
  };

  // Get participant names for display
  const getParticipantNames = () => {
    if (!thread?.participants) return 'Unknown';
    const names = thread.participants.map(p => p.full_name || 'Unknown').filter(Boolean);
    if (names.length <= 2) return names.join(' & ');
    return `${names[0]} & ${names.length - 1} others`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#004B8D]"></div>
      </div>
    );
  }

  const other = getOtherParticipant();
  const otherPhotoUrl = getProfilePhotoUrl(other.profile_photo);
  const isModerating = thread?.is_platform_owner_view;

  return (
    <div className={`min-h-screen flex flex-col ${isModerating ? 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 ${isModerating ? 'bg-black/50 backdrop-blur-xl border-b border-white/10' : 'bg-white border-b border-slate-100'}`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(getBackPath())} 
            className={`h-9 w-9 ${isModerating ? 'text-white hover:bg-white/10' : ''}`}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {isModerating ? (
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-yellow-400" />
                </div>
              ) : otherPhotoUrl ? (
                <img 
                  src={otherPhotoUrl}
                  alt={other.full_name}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList?.remove('hidden');
                  }}
                />
              ) : null}
              {!isModerating && (
                <div className={`w-10 h-10 rounded-full bg-[#004B8D] flex items-center justify-center text-white text-sm font-semibold ${otherPhotoUrl ? 'hidden' : ''}`}>
                  {other.full_name?.charAt(0)?.toUpperCase() || <User className="w-5 h-5" />}
                </div>
              )}
            </div>

            <div className="min-w-0">
              <h1 className={`font-medium text-sm sm:text-base truncate ${isModerating ? 'text-white' : ''}`}>
                {isModerating ? getParticipantNames() : other.full_name}
              </h1>
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary" className={`text-[10px] flex items-center gap-1 ${isModerating ? 'bg-purple-500/20 text-purple-300 border-0' : ''}`}>
                  {getThreadTypeIcon(thread?.thread_type)}
                  {getThreadTypeLabel(thread?.thread_type)}
                </Badge>
                {isModerating && (
                  <Badge className="text-[10px] bg-yellow-500/20 text-yellow-400 border-0">
                    Moderator View
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Context bar */}
        {(thread?.vehicle || thread?.booking) && (
          <div className={`px-4 py-2 border-t ${isModerating ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'}`}>
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 text-xs sm:text-sm">
              {thread?.vehicle && (
                <Link 
                  to={`/vehicles/${thread.vehicle.vehicle_id}`}
                  className={`flex items-center gap-2 ${isModerating ? 'text-white/60 hover:text-white' : 'text-slate-600 hover:text-[#004B8D]'}`}
                >
                  <Car className="w-4 h-4" />
                  <span>{thread.vehicle.make} {thread.vehicle.model}</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
              {thread?.booking && (
                <Link 
                  to={`/bookings/${thread.booking.booking_id}`}
                  className={`flex items-center gap-2 ${isModerating ? 'text-white/60 hover:text-white' : 'text-slate-600 hover:text-[#004B8D]'}`}
                >
                  <Calendar className="w-4 h-4" />
                  <span>View Booking</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Platform Owner Support Banner */}
        {isModerating && (
          <div className="bg-yellow-500/10 border-t border-yellow-500/30 px-4 py-2">
            <div className="max-w-4xl mx-auto flex items-center gap-2 text-xs text-yellow-200">
              <Shield className="w-4 h-4 text-yellow-400" />
              <span>Your messages will appear as <strong>"HireHub Support"</strong></span>
            </div>
          </div>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="messages-container">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 ? (
            <div className={`text-center py-12 ${isModerating ? 'text-white/50' : 'text-slate-500'}`}>
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === user?.user_id;
              const isHireHubSupport = msg.sender?.is_support || msg.sender_display === "HireHub Support";
              const senderPhotoUrl = getProfilePhotoUrl(msg.sender?.profile_photo);
              
              return (
                <div 
                  key={msg.message_id}
                  className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}
                  data-testid={`message-${msg.message_id}`}
                >
                  {/* Avatar - only show for others */}
                  {!isOwn && (
                    <div className="flex-shrink-0">
                      {isHireHubSupport ? (
                        <div className="w-8 h-8 rounded-full bg-[#004B8D] flex items-center justify-center">
                          <Shield className="w-4 h-4 text-white" />
                        </div>
                      ) : senderPhotoUrl ? (
                        <img 
                          src={senderPhotoUrl}
                          alt={msg.sender?.full_name}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling?.classList?.remove('hidden');
                          }}
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full ${isModerating ? 'bg-purple-600/50' : 'bg-slate-300'} flex items-center justify-center ${isModerating ? 'text-white' : 'text-slate-600'} text-xs font-semibold`}>
                          {msg.sender?.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Message bubble */}
                  <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
                    {/* Sender name for group chats or HireHub Support messages */}
                    {!isOwn && (thread?.participants?.length > 2 || isHireHubSupport) && (
                      <p className={`text-xs mb-1 ml-1 font-medium ${isHireHubSupport ? 'text-[#004B8D]' : isModerating ? 'text-white/50' : 'text-slate-500'}`}>
                        {isHireHubSupport ? 'HireHub Support' : msg.sender?.full_name}
                      </p>
                    )}
                    <div 
                      className={`rounded-2xl px-4 py-2.5 ${
                        isOwn 
                          ? isModerating 
                            ? 'bg-yellow-500/20 text-yellow-100 rounded-br-md border border-yellow-500/30' 
                            : 'bg-[#004B8D] text-white rounded-br-md'
                          : isHireHubSupport
                            ? isModerating
                              ? 'bg-[#004B8D]/20 border border-[#004B8D]/40 text-white rounded-bl-md'
                              : 'bg-[#004B8D]/10 border-2 border-[#004B8D]/30 text-slate-900 rounded-bl-md'
                            : isModerating 
                              ? 'bg-white/10 border border-white/20 text-white rounded-bl-md'
                              : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                    </div>
                    <p className={`text-[10px] mt-1 ${isOwn ? 'text-right mr-1' : 'ml-1'} ${isModerating ? 'text-white/40' : 'text-slate-400'}`}>
                      {formatTimestamp(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      {thread?.can_send ? (
        <div className={`px-4 py-3 safe-area-inset-bottom ${isModerating ? 'bg-black/30 border-t border-white/10' : 'bg-white border-t border-slate-100'}`}>
          <form onSubmit={handleSend} className="max-w-4xl mx-auto flex items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isModerating ? "Reply as HireHub Support..." : "Type a message..."}
              className={`flex-1 min-h-[44px] max-h-32 resize-none ${isModerating ? 'bg-white/10 border-white/20 text-white placeholder:text-white/50' : ''}`}
              rows={1}
              data-testid="message-input"
            />
            <Button 
              type="submit"
              disabled={!newMessage.trim() || sending}
              className={`h-11 w-11 p-0 ${isModerating ? 'bg-yellow-500/80 hover:bg-yellow-500' : 'bg-[#004B8D] hover:bg-[#003865]'}`}
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      ) : (
        <div className={`px-4 py-3 ${isModerating ? 'bg-white/5 border-t border-white/10' : 'bg-slate-100 border-t border-slate-200'}`}>
          <div className={`max-w-4xl mx-auto flex items-center justify-center gap-2 text-sm ${isModerating ? 'text-white/50' : 'text-slate-500'}`}>
            <Lock className="w-4 h-4" />
            <span>You can only view this conversation</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
